import type {
  Shape,
  WallPathEdge,
  WallPathShape,
  WallShape,
  ZoneShape
} from "@gettactic/map-builder/src/types";
import {
  bezierPointAndNormal,
  getWallControlPoint
} from "@gettactic/map-builder/src/utils/bezier";
import {
  computeWallPathBounds,
  createWallPath
} from "@gettactic/map-builder/src/utils/shapeFactory";
import { normalizeZonePolygon } from "@gettactic/map-builder/src/utils/zonePolygon";
import type { Point } from "../types";

/**
 * Pure edits of wall-path, zone-polygon and wall geometry — the same
 * operations the Konva shape components performed inline. Vertices are
 * world points for wall paths and walls, zone-local for zones.
 */

export const moveWallPathVertex = (
  path: WallPathShape,
  index: number,
  point: Point
): Partial<WallPathShape> => {
  const vertices = path.vertices.map((vertex, current) =>
    current === index
      ? { ...vertex, x: Math.round(point.x), y: Math.round(point.y) }
      : vertex
  );
  return { vertices, ...computeWallPathBounds(vertices) };
};

/** Inserts a vertex on edge `edgeIndex` (at its midpoint, or on the curve
 *  peak for a curved edge) and returns the index of the new vertex. */
export const insertWallPathVertex = (
  path: WallPathShape,
  edgeIndex: number
): { patch: Partial<WallPathShape>; vertexIndex: number } => {
  const from = path.vertices[edgeIndex];
  const to = path.vertices[(edgeIndex + 1) % path.vertices.length];
  const edge = path.edges[edgeIndex] || {};
  let point: Point;
  if (edge.curvature) {
    const control = getWallControlPoint(
      from.x,
      from.y,
      to.x,
      to.y,
      edge.curvature,
      edge.curvatureCenter ?? 0.5
    );
    const peak = bezierPointAndNormal(
      0,
      0,
      control.cx - from.x,
      control.cy - from.y,
      to.x - from.x,
      to.y - from.y,
      0.5
    );
    point = { x: from.x + peak.x, y: from.y + peak.y };
  } else {
    point = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  }
  const vertices = [
    ...path.vertices.slice(0, edgeIndex + 1),
    point,
    ...path.vertices.slice(edgeIndex + 1)
  ];
  const edges: WallPathEdge[] = [
    ...path.edges.slice(0, edgeIndex),
    {},
    {},
    ...path.edges.slice(edgeIndex + 1)
  ];
  return {
    patch: { vertices, edges, ...computeWallPathBounds(vertices) },
    vertexIndex: edgeIndex + 1
  };
};

/** Removes a vertex, keeping each surviving edge's data on the segment it
 *  belonged to. Returns null when the path would drop below two vertices. */
export const removeWallPathVertex = (
  path: WallPathShape,
  index: number
): Partial<WallPathShape> | null => {
  const count = path.vertices.length;
  if (count <= 2) return null;
  const closed = path.closed && count <= 3 ? false : path.closed;
  const vertices = path.vertices.filter((_, current) => current !== index);
  const expected = closed ? vertices.length : vertices.length - 1;
  const edges: WallPathEdge[] = [];
  for (let edgeIndex = 0; edgeIndex < expected; edgeIndex++) {
    const newFrom = edgeIndex;
    const newTo = (edgeIndex + 1) % vertices.length;
    const oldFrom = newFrom < index ? newFrom : newFrom + 1;
    const oldTo = newTo < index ? newTo : newTo + 1;
    edges.push(
      (oldFrom + 1) % count === oldTo ? path.edges[oldFrom] || {} : {}
    );
  }
  return { vertices, edges, closed, ...computeWallPathBounds(vertices) };
};

/** Splits an open path at a vertex into up to two paths, or opens a closed
 *  path there. Returns the shapes replacing the original. */
export const splitWallPathAtVertex = (
  path: WallPathShape,
  index: number
): Shape[] => {
  const count = path.vertices.length;
  if (path.closed) {
    const reordered: WallPathShape["vertices"] = [];
    for (let step = 1; step < count; step++)
      reordered.push(path.vertices[(index + step) % count]);
    const edges: WallPathEdge[] = [];
    for (let step = 0; step < count; step++) {
      const edgeIndex = (index + step) % count;
      const to = (edgeIndex + 1) % count;
      if (edgeIndex === index || to === index) continue;
      edges.push(path.edges[edgeIndex] || {});
    }
    return [
      {
        ...path,
        vertices: reordered,
        edges: fitEdges(edges, reordered.length - 1),
        closed: false,
        ...computeWallPathBounds(reordered)
      }
    ];
  }
  const first = path.vertices.slice(0, index);
  const second = path.vertices.slice(index + 1);
  const result: Shape[] = [];
  if (first.length >= 2) {
    result.push(
      createWallPath(first, {
        closed: false,
        edges: path.edges.slice(0, Math.max(0, index - 1))
      })
    );
  }
  if (second.length >= 2) {
    result.push(
      createWallPath(second, {
        closed: false,
        edges: path.edges.slice(index + 1)
      })
    );
  }
  return result;
};

/** Splits a path at an edge (right-click on a midpoint). */
export const splitWallPathAtEdge = (
  path: WallPathShape,
  edgeIndex: number
): Shape[] => {
  if (path.closed) {
    const vertices = [
      ...path.vertices.slice(edgeIndex + 1),
      ...path.vertices.slice(0, edgeIndex + 1)
    ];
    const edges = [
      ...path.edges.slice(edgeIndex + 1),
      ...path.edges.slice(0, edgeIndex)
    ];
    return [
      {
        ...path,
        vertices,
        edges: fitEdges(edges, vertices.length - 1),
        closed: false,
        ...computeWallPathBounds(vertices)
      }
    ];
  }
  const first = path.vertices.slice(0, edgeIndex + 1);
  const second = path.vertices.slice(edgeIndex + 1);
  const result: Shape[] = [];
  if (first.length >= 2) {
    result.push(
      createWallPath(first, {
        closed: false,
        edges: path.edges.slice(0, edgeIndex)
      })
    );
  }
  if (second.length >= 2) {
    result.push(
      createWallPath(second, {
        closed: false,
        edges: path.edges.slice(edgeIndex + 1)
      })
    );
  }
  return result;
};

const fitEdges = (edges: WallPathEdge[], count: number): WallPathEdge[] => {
  const result = edges.slice(0, Math.max(0, count));
  while (result.length < count) result.push({});
  return result;
};

/** Curvature of an edge from where its control dot was dragged to. */
export const curvatureFromPoint = (
  from: Point,
  to: Point,
  point: Point
): { curvature: number; curvatureCenter: number } | null => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) return null;
  const ux = dx / length;
  const uy = dy / length;
  const lx = point.x - from.x;
  const ly = point.y - from.y;
  const along = lx * ux + ly * uy;
  const perp = lx * -uy + ly * ux;
  return {
    curvature: Math.round(perp),
    curvatureCenter:
      Math.round(Math.max(0.05, Math.min(0.95, along / length)) * 100) / 100
  };
};

export const setWallPathEdgeCurvature = (
  path: WallPathShape,
  edgeIndex: number,
  curve: { curvature: number; curvatureCenter: number }
): Partial<WallPathShape> => {
  const edges = [...path.edges];
  edges[edgeIndex] = { ...edges[edgeIndex], ...curve };
  return { edges };
};

export const bendWallPathEdge = (
  path: WallPathShape,
  edgeIndex: number
): Partial<WallPathShape> => {
  const from = path.vertices[edgeIndex];
  const to = path.vertices[(edgeIndex + 1) % path.vertices.length];
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  return setWallPathEdgeCurvature(path, edgeIndex, {
    curvature: Math.round(length * 0.25),
    curvatureCenter: 0.5
  });
};

export const moveZoneVertex = (
  zone: ZoneShape,
  index: number,
  world: Point
): Partial<ZoneShape> => {
  const vertices = (zone.vertices ?? []).map((vertex, current) =>
    current === index
      ? { x: Math.round(world.x - zone.x), y: Math.round(world.y - zone.y) }
      : vertex
  );
  return normalizeZonePolygon(
    zone,
    vertices,
    zone.edges ?? vertices.map(() => ({}))
  );
};

export const setZoneEdgeCurvature = (
  zone: ZoneShape,
  edgeIndex: number,
  curve: { curvature: number; curvatureCenter: number }
): Partial<ZoneShape> => {
  const vertices = zone.vertices ?? [];
  const edges = [...(zone.edges ?? [])];
  while (edges.length < vertices.length) edges.push({});
  edges[edgeIndex] = curve;
  return normalizeZonePolygon(zone, vertices, edges);
};

export const moveWallEndpoint = (
  end: "start" | "end",
  point: Point
): Partial<WallShape> => {
  const rounded = { x: Math.round(point.x), y: Math.round(point.y) };
  return end === "start"
    ? { x: rounded.x, y: rounded.y }
    : { x2: rounded.x, y2: rounded.y };
};
